"use client"

import type { TabNode } from "flexlayout-react"
import { useEditorStore } from "@/lib/editor-store"
import { SynctexPdfViewer } from "./synctex-pdf-viewer"
import { basename } from "@/lib/compiler/path-utils"
import { hasAuthSession } from "@/lib/auth-client"

interface PDFPreviewPanelProps {
  node: TabNode
}

interface PdfTabConfig {
  /** The tex file this tab was compiled from. Undefined on the initial placeholder tab. */
  compiledFile?: string
  /** The PDF url captured at compile time for this specific file. */
  pdfUrl?: string
}

export function PDFPreviewPanel({ node }: PDFPreviewPanelProps) {
  const { state} = useEditorStore()

  const config = (node.getConfig() ?? {}) as PdfTabConfig

  const isAuth = hasAuthSession()

  // If this tab has its own compiled PDF (set by DockableLayout on success), use it.
  // Otherwise fall back to the global state (placeholder tab before first compile).
  const tabCompiledFile = config.compiledFile
  const tabPdfUrl = isAuth
    ? (tabCompiledFile ? (state.pdfByFile[tabCompiledFile] ?? config.pdfUrl) : state.pdfUrl)
    : (state.pdfByFile[state.mainFile || "main.tex"] ?? state.pdfUrl)

  const compiledFile = isAuth ? (tabCompiledFile ?? state.compiledFile) : (state.mainFile || "main.tex")
  const activeFile = state.activeFile

  // Only hide the PDF when a DIFFERENT tex file is active AND this is still the
  // placeholder tab (no dedicated compiled file yet).
  const activeIsUncompiledTex = isAuth && (
    !tabCompiledFile &&
    Boolean(activeFile) &&
    activeFile!.endsWith(".tex") &&
    Boolean(compiledFile) &&
    activeFile !== compiledFile
  )

  const effectivePdfUrl = activeIsUncompiledTex ? undefined : tabPdfUrl
  Boolean(effectivePdfUrl);
  const mainFile = state.mainFile || "main.tex"
  if (!state.sessionId) {
  } else if (state.compileStatus === "compiling") {
    `Compiling ${compiledFile ? basename(compiledFile) : activeFile ?? "PDF"}…`
  } else if (!isAuth && activeFile && activeFile !== mainFile) {
    `Switch to ${basename(mainFile)} to compile`
  } else if (state.compileStatus === "error") {
  } else if (activeIsUncompiledTex) {
    `${basename(activeFile!)} not compiled — click Compile to generate PDF`
  } else if (!effectivePdfUrl) {
  } else if (compiledFile) {
    `PDF for ${basename(compiledFile)}`
  }

  return (
    <div className="flex flex-col h-full bg-[var(--ed-preview-bg-alt)]">

      <div className="flex-1 min-h-0 overflow-hidden">
        <SynctexPdfViewer pdfUrl={effectivePdfUrl} tabId={node.getId()} />
      </div>

      {state.compileError && (
        <div className="border-t border-[var(--ed-pdf-error-border)] bg-[var(--ed-pdf-error-bg)] px-3 py-2 text-xs text-[var(--ed-pdf-error-text)]">
          <div className="font-semibold mb-1">Compile error: {state.compileError}</div>
          {state.compileErrorDetails && (
            <pre className="whitespace-pre-wrap break-all font-mono text-[11px] opacity-80 max-h-40 overflow-y-auto mt-1">
              {state.compileErrorDetails}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}
